import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  limit,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase.config";
import Navbar from "../Components/Navbar";
import { useNavigate } from "react-router-dom";

const Attendance = () => {
  const [userId, setUserId] = useState(null);
  const [userInfo, setUserInfo] = useState({});
  const [attendanceData, setAttendanceData] = useState([]);
  const [isMarkingAttendance, setIsMarkingAttendance] = useState(false);
  const [alreadyMarked, setAlreadyMarked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUserId = JSON.parse(localStorage.getItem("userid"));
    if (storedUserId) {
      setUserId(storedUserId);
    } else {
      setUserId(null);
    }
  }, [navigate]);

  useEffect(() => {
    if (userId) {
      const fetchUserInfo = async () => {
        try {
          const userDocRef = doc(db, "users", userId);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            setUserInfo(userDocSnap.data());
          } else {
            console.log("User does not exist.");
          }

          const attendanceQuery = query(
            collection(db, "attendance"),
            where("userId", "==", userId),
            limit(1)
          );
          const querySnapshot = await getDocs(attendanceQuery);
          const userAttendance = querySnapshot.docs.map((doc) => doc.data());

          setAttendanceData(userAttendance);

          if (userAttendance.length > 0) {
            const now = new Date();
            const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
            const recentAttendance =
              new Date(userAttendance[0].timestamp) > oneHourAgo;

            setAlreadyMarked(recentAttendance);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      };

      fetchUserInfo();
    }
  }, [userId]);

  const handleMarkPresent = () => {
    if (!userId || !userInfo.name) {
      alert("User information is incomplete. Please log in again.");
      return;
    }

    setIsMarkingAttendance(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
        const timestamp = new Date().toISOString();

        try {
          await addDoc(collection(db, "attendance"), {
            userId: userId,
            name: userInfo.name || "Unknown",
            latitude: latitude || 0,
            longitude: longitude || 0,
            timestamp: timestamp,
          });
          setIsMarkingAttendance(false);
          alert("Attendance marked successfully!");
          window.location.reload();
        } catch (error) {
          setIsMarkingAttendance(false);
          console.error("Error marking attendance:", error);
        }
      });
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const openInGoogleMaps = (latitude, longitude) => {
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, "_blank");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-gray-100">
      <Navbar />
      <div className="max-w-4xl mx-auto p-4 sm:p-8">
        <div className="bg-gray-800 shadow-xl rounded-lg p-4 sm:p-8">
          {userId ? (
            <>
              <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">
                Attendance
              </h2>
              <div className="bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg sm:text-2xl font-semibold mb-2">
                  User Information
                </h3>
                <p className="text-sm sm:text-base">
                  <strong>Name:</strong> {userInfo.name || "N/A"}
                </p>
                <p className="text-sm sm:text-base">
                  <strong>Email:</strong> {userInfo.email || "N/A"}
                </p>
                <p className="text-sm sm:text-base">
                  <strong>Department:</strong> {userInfo.department || "N/A"}
                </p>
                <p className="text-sm sm:text-base">
                  <strong>Club:</strong> {userInfo.club || "N/A"}
                </p>
                <p className="text-sm sm:text-base">
                  <strong>Role:</strong> {userInfo.role || "N/A"}
                </p>
              </div>

              {alreadyMarked ? (
                <div className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded-md text-sm sm:text-base">
                  You have already marked your attendance within the last hour.
                </div>
              ) : (
                <button
                  onClick={handleMarkPresent}
                  className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition duration-300 text-sm sm:text-base"
                  disabled={isMarkingAttendance}
                >
                  {isMarkingAttendance ? "Marking..." : "Mark Present"}
                </button>
              )}

              {/* Attendance Table */}
              <div className="mt-8 overflow-x-auto">
                <h3 className="text-lg sm:text-2xl font-semibold mb-4">
                  Attendance Records
                </h3>
                <table className="min-w-full bg-gray-700 text-white text-sm sm:text-base">
                  <thead>
                    <tr>
                      <th className="py-2 px-2 sm:px-4 border-b border-gray-600">
                        Name
                      </th>
                      <th className="py-2 px-2 sm:px-4 border-b border-gray-600">
                        Latitude
                      </th>
                      <th className="py-2 px-2 sm:px-4 border-b border-gray-600">
                        Longitude
                      </th>
                      <th className="py-2 px-2 sm:px-4 border-b border-gray-600">
                        Timestamp
                      </th>
                      <th className="py-2 px-2 sm:px-4 border-b border-gray-600">
                        Map
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceData.map((record, index) => (
                      <tr key={index}>
                        <td className="py-2 px-2 sm:px-4 border-b border-gray-600">
                          {record.name || "N/A"}
                        </td>
                        <td className="py-2 px-2 sm:px-4 border-b border-gray-600">
                          {record.latitude || "N/A"}
                        </td>
                        <td className="py-2 px-2 sm:px-4 border-b border-gray-600">
                          {record.longitude || "N/A"}
                        </td>
                        <td className="py-2 px-2 sm:px-4 border-b border-gray-600">
                          {new Date(record.timestamp).toLocaleString() || "N/A"}
                        </td>
                        <td className="py-2 px-2 sm:px-4 border-b border-gray-600">
                          <button
                            onClick={() =>
                              openInGoogleMaps(
                                record.latitude,
                                record.longitude
                              )
                            }
                            className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-300 text-sm sm:text-base"
                          >
                            View on Map
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-gray-100 flex items-center justify-center p-4">
              <div className="text-center">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                  You need to log in to view this page.
                </h2>
                <div className="overflow-x-auto w-full">
                  <table className="min-w-full bg-gray-700 text-white text-sm sm:text-base">
                    <thead>
                      <tr>
                        <th className="py-2 px-2 sm:px-4 border-b border-gray-600">
                          Name
                        </th>
                        <th className="py-2 px-2 sm:px-4 border-b border-gray-600">
                          Latitude
                        </th>
                        <th className="py-2 px-2 sm:px-4 border-b border-gray-600">
                          Longitude
                        </th>
                        <th className="py-2 px-2 sm:px-4 border-b border-gray-600">
                          Timestamp
                        </th>
                      </tr>
                    </thead>
                    <tbody>{/* No records to show if not logged in */}</tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Attendance;
